<template>
  <b-tab :title="title" lazy class="tab__assess">
    <template slot="title">
      <span ref="title" class="font-size-md active span__publication-todo-tab mr-2">{{
        title
      }}</span>

      <div class="divider" />
    </template>

    <div class="justify-content-center">
      <div class="font-weight-bold mb-5">View submission and feedback</div>
      <!-- Search & Filter -->

      <transition name="slide-fade">
        <!-- Your component's content here -->
        <BaseFilterStatusSubmission
          v-if="!isLoading.loadingStatusAllEvidenceSubmission"
          v-model="filterOption"
          :learning-unit-id="learningUnitId"
          :evidence-submissions="evidenceSubmissions"
          :show="showFilter"
          :static="isStaticStats"
          :static-stats="staticStats"
          @change="fetchEvidenceSubmission"
        />
      </transition>

      <div class="mt-u6 d-flex justify-content-between">
        <div class="flex-grow-1 mr-u6" style="max-width: 520px">
          <SearchBar
            v-model="searchKeywords"
            placeholder="Search submission ID here"
            @search="fetchEvidenceSubmission"
            @reset="reset"
          />
        </div>
      </div>

      <!-- Submission table -->
      <transition v-if="!isLoading.loadingEvidenceSubmission" name="fade">
        <EvidenceSubmissionList
          v-model="currentPage"
          :evidence-submissions="searchResults"
          :total-items="totalItems"
          :per-page="perPage"
          :fields="fields"
          :user-id="userId"
          :mode="mode"
        />
      </transition>
    </div>
  </b-tab>
</template>

<script>
import LearningUnit from '@/models/LearningUnit';
import EvidenceSubmission from '@/models/EvidenceSubmission';
import EvidenceSubmissionList from '@/components/EvidenceSubmission/EvidenceSubmissionList.vue';
import SearchBar from '@/views/LearningUnit/components/SearchBar.vue';
import BaseFilterStatusSubmission from '@/components/base-components/BaseFilterStatusSubmission.vue';
import { debounce, kebabCase } from 'lodash';
import { omit } from 'lodash';

export default {
  components: {
    EvidenceSubmissionList,
    SearchBar,
    BaseFilterStatusSubmission
  },

  props: {
    title: {
      type: String,
      default: ''
    },

    assessorId: {
      type: Number,
      default: null
    },

    active: {
      type: [Number, Boolean],
      default: false
    },

    mode: {
      type: String,
      default: 'assessor'
    }
  },
  data() {
    return {
      isLoading: {
        loadingEvidenceSubmission: false,
        loadingStatusAllEvidenceSubmission: false
      },
      fields: [
        { key: 'code', label: 'SUBMISSION ID', thClass: 'bg-white' },
        { key: 'submitted_no', label: 'SUBMITTED NO.', thClass: 'bg-white' },
        { key: 'submitted_at', label: 'SUBMITTED DATE', thClass: 'bg-white' },
        { key: 'status', label: 'STATUS', thClass: 'bg-white' },
        { key: 'action', label: '', thClass: 'bg-white' }
      ],
      filterOption: this.$route.query?.status
        ? this.$route.query.status.replaceAll('-', '_').toUpperCase()
        : 'ALL',
      searchKeywords: [],
      searchResults: [],
      showFilter: [
        'All',
        EvidenceSubmission.STATUS.IN_PROGRESS_BY_ASSESSOR,
        EvidenceSubmission.STATUS.IN_PROGRESS_BY_ISSUER,
        EvidenceSubmission.STATUS.AWARDED,
        EvidenceSubmission.STATUS.DENIED
      ],
      totalItems: 0,
      currentPage: this.$route.query?.page ? parseInt(this.$route.query.page) : 1,
      perPage: 20,
      isStaticStats: false,
      staticStats: {}
    };
  },

  computed: {
    fallbackRoute() {
      return {
        name: 'design-planning/info',
        params: {
          learningUnitId: this.learningUnitId
        },
        query: {
          tab: 'evaluation-assessments'
        }
      };
    },

    learningUnitId() {
      return parseInt(this.$route.params.learningUnitId);
    },

    learningUnit() {
      return LearningUnit.find(this.learningUnitId);
    },

    userId() {
      return this.$currentUser ? this.$currentUser.id : null;
    },

    evidenceSubmissions() {
      return EvidenceSubmission.query()
        .where({ learning_unit_id: this.learningUnitId })
        .with('learning_unit')
        .orderBy('submitted_at', 'desc')
        .get();
    },

    filterOptions() {
      return [
        { value: null, text: 'All' },
        {
          value: 'REVIEW_PENDING',
          text: EvidenceSubmission.STATUS.REVIEW_PENDING
        },
        { value: 'DENIED', text: EvidenceSubmission.STATUS.DENIED },
        { value: 'AWARDED', text: EvidenceSubmission.STATUS.AWARDED }
      ];
    },

    isLoadingDone() {
      return Object.values(this.isLoading).every((value) => value === false);
    }
  },

  watch: {
    active: {
      immediate: true,
      handler(active) {
        if (active) {
          this.setDefaultValues();
          this.init();
          this.fetchStatusAllEvidenceSubmission();
        }
      }
    },

    currentPage: {
      handler(currentPage, previousPage) {
        if (currentPage) {
          this.fetchEvidenceSubmission({ currentPage });
        }

        if (currentPage != previousPage && previousPage != undefined) {
          this.$router.replace({
            query: { ...this.$route.query, page: currentPage }
          });
        }
      }
    },

    filterOption: {
      immediate: false,
      handler(filterOption) {
        this.$router.replace({
          query: { ...this.$route.query, status: kebabCase(filterOption) }
        });
      }
    }
  },

  created() {
    this.fetchEvidenceSubmission = debounce(this.fetchEvidenceSubmission, 500, {
      leading: true,
      trailing: false
    });

    this.fetchStatusAllEvidenceSubmission = debounce(this.fetchStatusAllEvidenceSubmission, 500, {
      leading: true,
      trailing: false
    });
  },

  methods: {
    setDefaultValues() {
      this.currentPage = 1;
      this.searchKeywords = [];
      this.filterOption = 'ALL';
    },

    async init() {
      const { currentPage } = this;

      /* fetch learning unit */
      if (!this.learningUnit) {
        await LearningUnit.api().fetchByLearningUnitId(this.learningUnitId);
      }

      /* fetch evidence submission */
      EvidenceSubmission.deleteAll();

      await this.fetchEvidenceSubmission({ currentPage });
    },

    async fetchEvidenceSubmission({ currentPage = 1 }) {
      this.$set(this.isLoading, 'loadingEvidenceSubmission', true);
      this.currentPage = currentPage;
      const filter = {
        _start: (this.currentPage - 1) * this.perPage,
        _limit: this.perPage,
        submitted_at_null: false,
        assessor: this.assessorId ?? undefined,
        _sort: 'submitted_at:desc'
      };

      const populate = ['assessor'];

      if (this.searchKeywords.length && !this.searchKeywords.includes('')) {
        const searchParams = this.searchKeywords.map((keyword) => keyword.toUpperCase());

        filter.code_contains = searchParams;
      }

      if (this.learningUnitId) {
        filter['learning_unit.id'] = this.learningUnitId;
      }

      if (this.filterOption !== 'ALL') {
        filter.status = this.filterOption;
      }

      if (this.filterOption == 'IN_PROGRESS_BY_ASSESSOR') {
        filter.status = 'REVIEW_PENDING';
        filter.assessor_reviewed_at_null = true;
      }

      if (this.filterOption == 'IN_PROGRESS_BY_ISSUER') {
        filter.status = 'REVIEW_PENDING';
        filter.assessor_reviewed_at_null = false;
        filter.issuer_reviewed_at_null = true;
      }

      /* fetch evidence submission */

      const {
        response: {
          data: { data, totalItems }
        }
      } = await LearningUnit.api().fetchEvidenceSubmissionsWithPagination(this.learningUnitId, {
        ...filter,
        populate
      });

      this.totalItems = totalItems;

      if (data.length) {
        this.searchResults = data.map((submission) => {
          return {
            ...submission,
            user_id: submission.user,
            learning_unit_id: submission.learning_unit,
            assessor_id: submission.assessor?.id ?? null
          };
        });
      } else {
        this.searchResults = [];
      }
      this.$set(this.isLoading, 'loadingEvidenceSubmission', false);
    },

    async fetchStatusAllEvidenceSubmission() {
      if (this.assessorId === null) return;
      this.$set(this.isLoading, 'loadingStatusAllEvidenceSubmission', true);
      const {
        response: { data }
      } = await LearningUnit.api().fetchStatusAllEvidenceSubmission(this.learningUnitId, {
        status_ne: 'NOT_SUBMITTED',
        assessor: this.userId
      });

      if (data) {
        this.staticStats = { ...omit(data, ['total_not_submitted']) };
        this.isStaticStats = true;
      }

      this.$set(this.isLoading, 'loadingStatusAllEvidenceSubmission', false);
    },

    reset() {
      this.searchKeywords = [];
      this.fetchEvidenceSubmission({ currentPage: 1 });
    }
  }
};
</script>

<style lang="scss">
.slide-fade-enter-active {
  transition: all 0.5s ease;
}
</style>
